export interface Rect { x: number; y: number; w: number; h: number }
export interface StrokePoint { x: number; y: number; pressure: number; dt: number }

export type PenTool = 'pen' | 'highlighter';

export interface Stroke {
  id: string;          // ulid
  pageId: string;
  tool: PenTool;
  color: string;       // semantic token (tokens.ts)
  baseWidth: number;   // px at scale 1
  points: Float32Array; // packed [x, y, pressure, dt] quads
  bbox: Rect;
  createdAt: number;
  deletedAt?: number;  // tombstone
}

/** Append-only growable packed point buffer for the in-progress stroke. */
export class StrokeBuilder {
  private buf: Float32Array;
  private len = 0; // floats used
  constructor(initialPoints = 256) {
    this.buf = new Float32Array(initialPoints * 4);
  }
  get count(): number { return this.len / 4; }
  add(x: number, y: number, pressure: number, dt: number): void {
    if (this.len + 4 > this.buf.length) {
      const next = new Float32Array(this.buf.length * 2);
      next.set(this.buf);
      this.buf = next;
    }
    this.buf[this.len++] = x; this.buf[this.len++] = y;
    this.buf[this.len++] = pressure; this.buf[this.len++] = dt;
  }
  /** Copy of only the used portion. */
  toFloat32(): Float32Array { return this.buf.slice(0, this.len); }
  /** Zero-copy view for rendering the live stroke. */
  view(): Float32Array { return this.buf.subarray(0, this.len); }
  /** Replace the builder's entire contents with a packed buffer (shape-snap swap/revert). */
  replaceWith(points: Float32Array): void {
    if (points.length > this.buf.length) {
      this.buf = new Float32Array(Math.max(points.length, this.buf.length * 2));
    }
    this.buf.set(points);
    this.len = points.length;
  }
}

export function unpackPoints(points: Float32Array): StrokePoint[] {
  const out: StrokePoint[] = [];
  for (let i = 0; i + 3 < points.length; i += 4) {
    out.push({ x: points[i]!, y: points[i + 1]!, pressure: points[i + 2]!, dt: points[i + 3]! });
  }
  return out;
}

export function computeBbox(points: Float32Array, maxWidth: number): Rect {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i + 3 < points.length; i += 4) {
    const x = points[i]!, y = points[i + 1]!;
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const r = maxWidth / 2;
  if (!isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX - r, y: minY - r, w: maxX - minX + 2 * r, h: maxY - minY + 2 * r };
}

export function rectsIntersect(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
